// pages/DecorationGalleryPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { dbOnValue } from '../config/firebase';
import { ArrowLeft, Sparkles, Image as ImageIcon, ChevronLeft, ChevronRight, X } from 'lucide-react';

// ------------------- Lightbox Component (with swipe) -------------------
const Lightbox = ({ images, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const minSwipeDistance = 50;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex]);

  const goPrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };
  const goNext = () => {
    if (currentIndex < images.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const onTouchStartHandler = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const onTouchMoveHandler = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  const onTouchEndHandler = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) goNext();
      else goPrev();
    }
    setTouchStart(null);
    setTouchEnd(null);
  };

  const currentImage = images[currentIndex];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.95)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}
      onTouchStart={onTouchStartHandler}
      onTouchMove={onTouchMoveHandler}
      onTouchEnd={onTouchEndHandler}
    >
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 20,
          right: 20,
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: '#dc2626',
          border: 'none',
          color: 'white',
          cursor: 'pointer',
          zIndex: 2002,
        }}
      >
        <X size={20} />
      </button>

      {images.length > 1 && !isMobile && (
        <>
          {currentIndex > 0 && (
            <button
              onClick={goPrev}
              style={{
                position: 'absolute',
                left: 20,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.5)',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
              }}
            >
              <ChevronLeft size={24} />
            </button>
          )}
          {currentIndex < images.length - 1 && (
            <button
              onClick={goNext}
              style={{
                position: 'absolute',
                right: 20,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.5)',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
              }}
            >
              <ChevronRight size={24} />
            </button>
          )}
        </>
      )}

      <img
        src={currentImage?.url}
        alt={`Slide ${currentIndex + 1}`}
        style={{
          maxWidth: '90vw',
          maxHeight: '85vh',
          objectFit: 'contain',
          borderRadius: 8,
        }}
      />

      <div
        style={{
          position: 'absolute',
          bottom: 30,
          left: 0,
          right: 0,
          textAlign: 'center',
          color: 'white',
          background: 'rgba(0,0,0,0.6)',
          padding: '8px 16px',
          margin: '0 auto',
          width: 'fit-content',
          borderRadius: 30,
          fontSize: 14,
        }}
      >
        {currentIndex + 1} / {images.length}
      </div>

      {isMobile && images.length > 1 && (
        <div
          style={{
            position: 'absolute',
            bottom: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(0,0,0,0.5)',
            padding: '4px 12px',
            borderRadius: 20,
            fontSize: 11,
            color: 'white',
            pointerEvents: 'none',
          }}
        >
          ← Swipe to navigate →
        </div>
      )}
    </div>
  );
};

// ------------------- Category Page (Gallery Grid) -------------------
const CategoryGallery = ({ category, items, onBack, onImageClick }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#fff5f5' }}>
      {/* Header */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: '#dc2626',
          padding: isMobile ? '12px 16px' : '16px 24px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            onClick={onBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              padding: isMobile ? '6px 12px' : '8px 16px',
              borderRadius: 8,
              cursor: 'pointer',
              color: 'white',
              fontWeight: 500,
            }}
          >
            <ArrowLeft size={isMobile ? 14 : 16} /> Back to Categories
          </button>
          <h1 style={{ fontSize: isMobile ? '1rem' : '1.3rem', fontWeight: 700, margin: 0, color: 'white' }}>
            {category.name}
          </h1>
        </div>
      </div>

      {/* Gallery Grid */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '16px' : '24px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: isMobile ? 12 : 24,
          }}
        >
          {items.map((item) => {
            const cover = item.images?.[0]?.url;
            const imageCount = item.images?.length || 0;
            return (
              <div
                key={item.id}
                onClick={() => onImageClick(item)}
                style={{
                  background: 'white',
                  borderRadius: 12,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  border: '1px solid rgba(220,38,38,0.1)',
                  transition: 'transform 0.2s, boxShadow 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(220,38,38,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                }}
              >
                <div style={{ aspectRatio: '4/3', overflow: 'hidden', background: '#f8fafc', position: 'relative' }}>
                  {cover ? (
                    <img src={cover} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ImageIcon size={40} color="#cbd5e1" />
                    </div>
                  )}
                  {imageCount > 1 && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        background: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: 20,
                        fontSize: 11,
                      }}
                    >
                      {imageCount} photos
                    </div>
                  )}
                </div>
                <div style={{ padding: 12, textAlign: 'center' }}>
                  <h3 style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, margin: 0, color: '#1a0a0a' }}>
                    {item.title}
                  </h3>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ------------------- Main Component -------------------
const DecorationGalleryPage = () => {
  const navigate = useNavigate();
  const { categoryId, itemId } = useParams();
  const [categories, setCategories] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [lightboxItem, setLightboxItem] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Responsive
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load categories
  useEffect(() => {
    const unsubscribe = dbOnValue('decoration_gallery_categories', (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const cats = Object.entries(data).map(([key, cat]) => ({ id: key, ...cat }));
        setCategories(cats);
      } else {
        setCategories([
          { id: 'wedding', name: 'Wedding' },
          { id: 'birthday', name: 'Birthday' },
          { id: 'anniversary', name: 'Anniversary' },
          { id: 'corporate', name: 'Corporate' },
        ]);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Load gallery items
  useEffect(() => {
    const unsubscribe = dbOnValue('decoration_gallery', (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const items = Object.entries(data).map(([key, item]) => ({
          id: key,
          ...item,
          images: item.images || (item.url ? [{ url: item.url, imageId: item.imageId }] : []),
        }));
        setGalleryItems(items);
      } else {
        setGalleryItems([]);
      }
    });
    return () => unsubscribe();
  }, []);

  // Handle URL params for direct category or item view
  useEffect(() => {
    if (!loading && categories.length && galleryItems.length) {
      // If itemId is present, open lightbox for that item
      if (itemId) {
        const item = galleryItems.find(i => i.id === itemId);
        if (item) {
          setLightboxItem(item);
          setLightboxIndex(0);
        } else {
          // If item not found, maybe go to its category
          const parentCategory = categories.find(cat =>
            galleryItems.some(i => i.categoryId === cat.id && i.id === itemId)
          );
          if (parentCategory) setSelectedCategory(parentCategory);
        }
      }
      // If categoryId is present, show that category directly
      if (categoryId && !selectedCategory) {
        const cat = categories.find(c => c.id === categoryId);
        if (cat) setSelectedCategory(cat);
      }
    }
  }, [loading, categories, galleryItems, categoryId, itemId]);

  const getCategoryPreview = (catId) => {
    const firstItem = galleryItems.find(item => item.categoryId === catId);
    return firstItem?.images?.[0]?.url || null;
  };

  const handleCategoryClick = (category) => {
    // Navigate to category route
    navigate(`/decoration-gallery/category/${category.id}`);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    navigate('/decoration-gallery');
  };

  const handleImageClick = (item) => {
    // Navigate to item route to open lightbox
    navigate(`/decoration-gallery/item/${item.id}`);
  };

  const closeLightbox = () => {
    setLightboxItem(null);
    // Go back to category view
    if (selectedCategory) {
      navigate(`/decoration-gallery/category/${selectedCategory.id}`);
    } else {
      navigate('/decoration-gallery');
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#fff5f5', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ width: 40, height: 40, border: '3px solid #dc2626', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
      </div>
    );
  }

  // If lightbox is open, show it on top
  if (lightboxItem) {
    return (
      <>
        {selectedCategory ? (
          <CategoryGallery
            category={selectedCategory}
            items={galleryItems.filter(i => i.categoryId === selectedCategory.id)}
            onBack={handleBackToCategories}
            onImageClick={(item) => {
              setLightboxItem(item);
              setLightboxIndex(0);
              navigate(`/decoration-gallery/item/${item.id}`);
            }}
          />
        ) : (
          <div style={{ minHeight: '100vh', background: '#fff5f5' }}>Loading...</div>
        )}
        <Lightbox
          images={lightboxItem.images}
          initialIndex={lightboxIndex}
          onClose={closeLightbox}
        />
      </>
    );
  }

  // If a category is selected, show its gallery
  if (selectedCategory) {
    const categoryItems = galleryItems.filter(item => item.categoryId === selectedCategory.id);
    return (
      <CategoryGallery
        category={selectedCategory}
        items={categoryItems}
        onBack={handleBackToCategories}
        onImageClick={handleImageClick}
      />
    );
  }

  // Otherwise show categories grid
  return (
    <div style={{ minHeight: '100vh', background: '#fff5f5' }}>
      {/* Header */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: '#dc2626',
          padding: isMobile ? '12px 16px' : '16px 24px',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button
            onClick={() => navigate("/")}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              padding: isMobile ? '6px 12px' : '8px 16px',
              borderRadius: 8,
              cursor: 'pointer',
              color: 'white',
              fontWeight: 500,
            }}
          >
            <ArrowLeft size={isMobile ? 14 : 16} /> Back
          </button>
          <h1 style={{ fontSize: isMobile ? '1rem' : '1.3rem', fontWeight: 700, margin: 0, color: 'white' }}>
            Decoration Gallery
          </h1>
          <div style={{ width: 70 }} />
        </div>
      </div>

      {/* Categories Grid */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '16px' : '24px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: isMobile ? 12 : 16,
          }}
        >
          {categories.map((cat) => {
            const preview = getCategoryPreview(cat.id);
            const itemCount = galleryItems.filter(i => i.categoryId === cat.id).length;
            return (
              <div
                key={cat.id}
                onClick={() => handleCategoryClick(cat)}
                style={{
                  cursor: 'pointer',
                  background: 'white',
                  borderRadius: 12,
                  overflow: 'hidden',
                  border: '1px solid rgba(220,38,38,0.15)',
                  transition: 'transform 0.2s, boxShadow 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div style={{ aspectRatio: '16/9', overflow: 'hidden', background: '#f8fafc' }}>
                  {preview ? (
                    <img src={preview} alt={cat.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ImageIcon size={32} color="#dc2626" opacity={0.4} />
                    </div>
                  )}
                </div>
                <div style={{ padding: 12, textAlign: 'center' }}>
                  <h3 style={{ fontSize: isMobile ? 14 : 16, fontWeight: 600, marginBottom: 4, color: '#1a0a0a' }}>
                    {cat.name}
                  </h3>
                  <p style={{ fontSize: 12, color: '#64748b' }}>
                    {itemCount} {itemCount === 1 ? 'design' : 'designs'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Floating Quote Button */}
      <button
        onClick={() => navigate('/decoration-enquiry')}
        style={{
          position: 'fixed',
          bottom: isMobile ? 70 : 24,
          right: 16,
          padding: isMobile ? '10px 18px' : '12px 24px',
          background: '#dc2626',
          color: 'white',
          border: 'none',
          borderRadius: 30,
          fontSize: isMobile ? 12 : 14,
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          boxShadow: '0 4px 15px rgba(220,38,38,0.3)',
          zIndex: 100,
        }}
      >
        <Sparkles size={isMobile ? 14 : 16} /> Get Quote
      </button>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default DecorationGalleryPage;